import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { FaTrash, FaEnvelope, FaPhone, FaBox, FaClock, FaEdit } from 'react-icons/fa';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export default function QuoteRequests() {
  const { i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [formData, setFormData] = useState({
    message: '',
    status: '',
    product_type: '',
    quantity: 1
  });

  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        const response = await axios.get(`${API}/quotes/admin/all?admin_id=${user.id}`);
        setQuotes(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching quote requests:', error);
        toast.error('Erreur lors du chargement des demandes');
        setLoading(false);
      }
    };

    if (user && (user.role === 'admin' || (user.roles && user.roles.includes('admin')))) {
      fetchQuotes();
    }
  }, [API, user]);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes/admin/all?admin_id=${user.id}`);
      setQuotes(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching quote requests:', error);
      toast.error('Erreur lors du chargement des demandes');
      setLoading(false);
    }
  };

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      await axios.put(
        `${API}/quotes/admin/update-status/${quoteId}?admin_id=${user.id}&status=${newStatus}`
      );
      toast.success('Statut mis à jour');
      fetchQuotes();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleEdit = (quote) => {
    setSelectedQuote(quote);
    setFormData({
      message: quote.message || '',
      status: quote.status || 'pending',
      product_type: quote.product_type || 'new',
      quantity: quote.quantity || 1
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    try {
      await axios.put(
        `${API}/quotes/admin/update/${selectedQuote.id}?admin_id=${user.id}`,
        formData
      );
      toast.success('Demande mise à jour');
      setEditDialogOpen(false);
      setSelectedQuote(null);
      fetchQuotes();
    } catch (error) {
      console.error('Error updating quote:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (quoteId) => {
    if (!confirm('Supprimer cette demande de devis ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/quotes/admin/delete/${quoteId}?admin_id=${user.id}`);
      toast.success('Demande supprimée');
      fetchQuotes();
    } catch (error) {
      console.error('Error deleting quote:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      contacted: 'bg-blue-100 text-blue-800',
      quoted: 'bg-[#635BFF]/10 text-[#5248d9]',
      closed: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      contacted: 'Contacté',
      quoted: 'Devis envoyé',
      closed: 'Fermé'
    };
    return labels[status] || status;
  };

  // Filtrer et trier par date (plus récentes en premier)
  const filteredQuotes = (filter === 'all' 
    ? quotes 
    : quotes.filter(q => q.status === filter))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const stats = {
    total: quotes.length,
    pending: quotes.filter(q => q.status === 'pending').length,
    contacted: quotes.filter(q => q.status === 'contacted').length,
    quoted: quotes.filter(q => q.status === 'quoted').length,
    closed: quotes.filter(q => q.status === 'closed').length
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <p>Chargement...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 md:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#0A2540] mb-2">
            Demandes de Devis
          </h1>
          <p className="text-gray-600">
            Gérez les demandes de devis des clients
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="cursor-pointer hover:shadow-lg transition" onClick={() => setFilter('all')}>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl font-bold text-[#0A2540]">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-lg transition" onClick={() => setFilter('pending')}>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">En attente</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-lg transition" onClick={() => setFilter('contacted')}>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Contacté</p>
              <p className="text-2xl font-bold text-blue-600">{stats.contacted}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-lg transition" onClick={() => setFilter('quoted')}>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Devis envoyé</p>
              <p className="text-2xl font-bold text-[#635BFF]">{stats.quoted}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-lg transition" onClick={() => setFilter('closed')}>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Fermé</p>
              <p className="text-2xl font-bold text-gray-600">{stats.closed}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="mb-6">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-full md:w-64">
              <SelectValue placeholder="Filtrer par statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous ({stats.total})</SelectItem>
              <SelectItem value="pending">En attente ({stats.pending})</SelectItem>
              <SelectItem value="contacted">Contacté ({stats.contacted})</SelectItem>
              <SelectItem value="quoted">Devis envoyé ({stats.quoted})</SelectItem>
              <SelectItem value="closed">Fermé ({stats.closed})</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Quotes List */}
        <div className="space-y-4">
          {filteredQuotes.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">
                Aucune demande de devis
              </CardContent>
            </Card>
          ) : (
            filteredQuotes.map((quote) => (
              <Card key={quote.id} className="hover:shadow-lg transition">
                <CardContent className="p-4 md:p-6">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    {/* Left: Quote Info */}
                    <div className="flex-1 space-y-3">
                      {/* Status Badge */}
                      <div>
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(quote.status)}`}>
                          {getStatusLabel(quote.status)}
                        </span>
                      </div>

                      {/* Customer Info */}
                      <div>
                        <h3 className="text-lg font-bold text-[#0A2540]">{quote.user_name}</h3>
                        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 text-sm text-gray-600 mt-1">
                          <div className="flex items-center gap-2">
                            <FaEnvelope className="text-gray-400" />
                            <a href={`mailto:${quote.user_email}`} className="hover:text-[#0A2540]">
                              {quote.user_email}
                            </a>
                          </div>
                          {quote.user_phone && (
                            <div className="flex items-center gap-2">
                              <FaPhone className="text-gray-400" />
                              <a href={`tel:${quote.user_phone}`} className="hover:text-[#0A2540]">
                                {quote.user_phone}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Product Info */}
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center gap-2 text-sm">
                          <FaBox className="text-[#0A2540]" />
                          <span className="font-semibold">{quote.product_name}</span>
                          <span className="text-gray-500">
                            • Quantité: {quote.quantity} • 
                            {quote.product_type === 'new' ? ' Neuf' : 
                             quote.product_type === 'used' ? ' Occasion' : ' Location'}
                          </span>
                        </div>
                      </div>

                      {/* Message */}
                      {quote.message && (
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <p className="text-sm text-gray-700">
                            <span className="font-semibold">Message:</span> {quote.message}
                          </p>
                        </div>
                      )}

                      {/* Date */}
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <FaClock />
                        <span>{new Date(quote.created_at).toLocaleString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</span>
                      </div>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex md:flex-col gap-2 md:min-w-[180px]">
                      <Select 
                        value={quote.status} 
                        onValueChange={(value) => handleStatusChange(quote.id, value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">En attente</SelectItem>
                          <SelectItem value="contacted">Contacté</SelectItem>
                          <SelectItem value="quoted">Devis envoyé</SelectItem>
                          <SelectItem value="closed">Fermé</SelectItem>
                        </SelectContent>
                      </Select>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(quote)}
                        className="md:w-full"
                      >
                        <FaEdit className="mr-2" />
                        Modifier
                      </Button>

                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(quote.id)}
                        className="md:w-full"
                      >
                        <FaTrash className="mr-2" />
                        Supprimer
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Modifier la demande de devis</DialogTitle>
            </DialogHeader>
            {selectedQuote && (
              <div className="space-y-4">
                <div>
                  <Label>Produit</Label>
                  <Input value={selectedQuote.product_name} disabled className="bg-gray-100" />
                </div>

                <div>
                  <Label>Client</Label>
                  <Input value={`${selectedQuote.user_name} (${selectedQuote.user_email})`} disabled className="bg-gray-100" />
                </div>

                <div>
                  <Label>Statut</Label>
                  <Select value={formData.status} onValueChange={(val) => setFormData({...formData, status: val})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">En attente</SelectItem>
                      <SelectItem value="contacted">Contacté</SelectItem>
                      <SelectItem value="quoted">Devis envoyé</SelectItem>
                      <SelectItem value="closed">Fermé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Type de produit</Label>
                  <Select value={formData.product_type} onValueChange={(val) => setFormData({...formData, product_type: val})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">Neuf</SelectItem>
                      <SelectItem value="used">Occasion</SelectItem>
                      <SelectItem value="rental">Location</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Quantité</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value) || 1})}
                  />
                </div>

                <div>
                  <Label>Message du client</Label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    rows={4}
                    placeholder="Message..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button onClick={handleUpdate} className="flex-1 bg-[#635BFF] hover:bg-[#5248d9]">
                    Enregistrer
                  </Button>
                  <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">
                    Annuler
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
